import { restApiCallback } from 'api/typed';
import { RestApiDefinition } from 'api/types';
import { TIMEOUT_CONFIG } from 'constants/checkout-api-timeout';

const get = {
  verbs: ['get'],
  service: 'order-payment',
  defaultVersion: 1,
  endpoint: 'failed-order-payments/${orderId}',
  timeout: TIMEOUT_CONFIG,
} as const satisfies RestApiDefinition;

const post = {
  verbs: ['post'],
  service: 'order-payment',
  defaultVersion: 1,
  endpoint: 'failed-order-payments/${orderId}/payment-cards',
  timeout: TIMEOUT_CONFIG,
} as const satisfies RestApiDefinition;

const patch = {
  verbs: ['patch'],
  service: 'order-payment',
  defaultVersion: 1,
  endpoint: 'failed-order-payments/${orderId}/payment-cards/${paymentCardId}',
  timeout: TIMEOUT_CONFIG,
} as const satisfies RestApiDefinition;

export type FailedOrderPaymentResponse = {
  // If true then this order is on a rolled out branch and the next boolean should be considered.
  // If false then no SSPR fullstop - do not show anything SSPR in the UI for this order at all.
  paymentResolutionEnabled: boolean;

  // If true then already paid. I guess this means no need to show any SSPR in the UI as it's "resolved", right...?
  // If false then not yet paid, consider the next boolean.
  resolved: boolean;

  // If true then the UI can show a link to "make payment now".
  // If false, then the UI should show a "contact customer care" (ie too many attempts, passed the fulfil date, etc, etc)
  paymentAttemptAllowed: boolean;

  // Technically only needed for the actual "sspr new payment" screen, but for all other calls, we're
  // still fetching the order from FOE so have this value anyway, so might as well just return it always.
  totalToPay: number;
};

export type PaymentCardForAuthorisation = {
  paymentCardId: string;
  addressId: string;
  securityTokenId: string;
  traSessionId?: string;
  metadata: {
    cardNumberLength: number;
    cardholderName: string;
    traEncryptedCardNumber?: string;
  };
};

export type PaymentCard = {
  paymentCardId: string;
  addressId: string;
  status: 'SECURITY_NEEDED' | 'SECURITY_CHECKED' | 'SECURITY_EXEMPT';
  metadata: {
    acsUrl: string;
    payerAuthAmount: number;
    payerAuthPayload: string;
    payerAuthTransactionId: string;
  };
};

export interface UpdatePaymentCard {
  authenticationResponseToken: string;
}

export const failedOrderPayment = {
  get: restApiCallback(get, 'get')<FailedOrderPaymentResponse>,
  post: restApiCallback(post, 'post')<
    PaymentCard,
    PaymentCardForAuthorisation,
    Record<string, string>
  >,
  patch: restApiCallback(patch, 'patch')<PaymentCard, UpdatePaymentCard>,
};
